import { m as motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const steps = [
  { num: "01", label: "Lyssna", tone: "mint", note: "30 min strategimöte. Vi börjar i ditt mål, inte i vår process." },
  { num: "02", label: "Forma", tone: "butter", note: "Seniora strateger formar vinkeln — du ser första klippet inom 10–14 dagar." },
  { num: "03", label: "Filma", tone: "peach", note: "Ett team genom hela kedjan. Fast månadspris, inga tilläggsfakturor." },
  { num: "04", label: "Skala", tone: "mint", note: "En film blir tio. Versioner per kanal, mätning av vad som presterar." },
] as const;

const dotTone = { mint: "bg-mint", butter: "bg-butter", peach: "bg-peach" } as const;
const textTone = { mint: "text-mint", butter: "text-butter", peach: "text-peach" } as const;

/** Diskret 4-stegs processrail med heartbeat. Förklarar Fennys modell utan att ta över heron. */
export function HeroProcessRail({ className }: { className?: string }) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const t = setInterval(() => setActive((i) => (i + 1) % steps.length), 2800);
    return () => clearInterval(t);
  }, [reduce]);

  return (
    <div
      className={cn(
        "relative w-full overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      aria-label="Fennys process: lyssna, forma, filma, skala"
    >
      <ol className="flex min-w-max items-center gap-0 md:min-w-0 md:grid md:grid-cols-4">
        {steps.map((s, i) => {
          const isActive = i === active;
          return (
            <li
              key={s.num}
              className={cn(
                "group relative flex snap-start items-center gap-3 border-t border-white/10 px-4 py-3 md:px-5 md:py-4",
                i === 0 && "md:border-l-0",
              )}
            >
              {/* heartbeat dot */}
              <span className="relative inline-flex h-1.5 w-1.5 shrink-0">
                <motion.span
                  aria-hidden
                  className={cn("absolute inset-0 rounded-full", dotTone[s.tone])}
                  animate={{
                    scale: isActive ? [1, 2.6, 1] : 1,
                    opacity: isActive ? [0.9, 0, 0.9] : 0.35,
                  }}
                  transition={{ duration: 1.6, ease: "easeOut" }}
                />
                <span
                  className={cn(
                    "relative inline-flex h-1.5 w-1.5 rounded-full transition-opacity",
                    dotTone[s.tone],
                    isActive ? "opacity-100" : "opacity-50",
                  )}
                />
              </span>

              <span
                className={cn(
                  "font-mono text-[10px] tracking-[0.18em] transition-colors",
                  isActive ? textTone[s.tone] : "text-muted-foreground/70",
                )}
              >
                {s.num}
              </span>
              <span
                className={cn(
                  "text-sm font-medium uppercase tracking-[0.14em] transition-colors md:text-[13px]",
                  isActive ? "text-foreground" : "text-foreground/65",
                )}
              >
                {s.label}
              </span>

              {/* tooltip on desktop hover */}
              <span
                aria-hidden
                className="pointer-events-none absolute left-4 top-full z-10 mt-2 hidden w-56 translate-y-1 rounded-lg border border-white/10 bg-surface/95 px-3 py-2 text-xs leading-snug text-foreground/85 opacity-0 shadow-xl backdrop-blur transition-all duration-200 md:block group-hover:translate-y-0 group-hover:opacity-100"
              >
                {s.note}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
